import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, ChevronRight, Gift, Star, Clock, Coffee, UtensilsCrossed, Sparkles, MapPin, Calendar, ArrowRight, Check, Shirt } from "lucide-react";

const MiniLogo = ({ name, size = "md" }) => {
  const colors = ["bg-sky-400", "bg-purple-400", "bg-green-400", "bg-orange-400", "bg-pink-400", "bg-indigo-400", "bg-teal-400", "bg-red-400"];
  const i = name ? name.charCodeAt(0) % colors.length : 0;
  const s = size === "sm" ? "w-9 h-9 text-xs" : size === "lg" ? "w-16 h-16 text-xl" : "w-11 h-11 text-sm";
  return <div className={`${s} rounded-2xl ${colors[i]} flex items-center justify-center flex-shrink-0`}><span className="text-white font-bold">{name?.charAt(0)?.toUpperCase()}</span></div>;
};

export const CompanionView = ({ data, user, onOfferClick }) => {
  const { getStatus } = useUnifiedStatus();
  const { offers, expiringSoon, bestValue, stats, favoriteIds, toggleFavorite } = data;
  const totalValue = offers.reduce((s, o) => s + (o.offer_value_est || 0), 0);

  // Personalized "plan" — 3-5 curated picks
  const plan = [];
  if (expiringSoon.length > 0) plan.push({ type: "urgent", offer: expiringSoon[0], message: "This one's expiring — grab it first!" });
  if (bestValue.length > 0) plan.push({ type: "value", offer: bestValue[0], message: "Highest value deal for you." });
  const easyOnes = offers.filter(o => o.ease_stars >= 4 && !plan.some(p => p.offer?.business_id === o.business_id)).slice(0, 2);
  easyOnes.forEach(o => plan.push({ type: "easy", offer: o, message: "Super easy to claim — no hassle." }));
  if (plan.length < 4) {
    const noApp = offers.filter(o => !o.offer_requires_app && !plan.some(p => p.offer?.business_id === o.business_id)).slice(0, 1);
    noApp.forEach(o => plan.push({ type: "noapp", offer: o, message: "No app download needed." }));
  }

  // Quick category summary
  const catCounts = {};
  offers.forEach(o => { catCounts[o.category] = (catCounts[o.category] || 0) + 1; });
  const topCats = Object.entries(catCounts).sort((a, b) => b[1] - a[1]).slice(0, 4);
  const catIcons = { "Restaurant": UtensilsCrossed, "Cafes & Drinks": Coffee, "Fast Food": UtensilsCrossed, "Bakery": Coffee, "Clothing & Apparel": Shirt, "Retail": Gift, "Dessert & Ice Cream": Sparkles, "Beauty & Skincare": Sparkles };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div className="min-h-screen bg-white pb-28" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Warm personal greeting */}
      <div className="px-6 pt-10 pb-2">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-slate-400 text-sm">{getGreeting()},</p>
          <h1 className="text-3xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {user?.first_name}
          </h1>
        </motion.div>
      </div>

      {/* Conversational birthday card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mx-6 mt-4 bg-sky-50 rounded-3xl p-5 border border-sky-100"
      >
        <p className="text-sky-900 text-sm leading-relaxed">
          Your birthday is in <strong className="text-sky-600">{stats.days_until_birthday} days</strong>.
          {stats.days_until_birthday <= 30
            ? " It's almost time! I've picked some priority birthday deals for you below."
            : " I've found "
          }
          <strong className="text-green-600">${totalValue.toFixed(0)}</strong> worth of free stuff across {offers.length} offers.
          {stats.days_until_birthday <= 30 ? "" : " Here's your plan:"}
        </p>
      </motion.div>

      {/* Today's Plan — curated picks */}
      <div className="px-6 mt-8">
        <h2 className="text-xs font-bold text-slate-400 tracking-widest uppercase mb-4">Your Priority Plan</h2>
        <div className="space-y-4">
          {plan.map((item, i) => (
            <motion.div
              key={item.offer.business_id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + i * 0.1 }}
              onClick={() => onOfferClick(item.offer)}
              className="bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] cursor-pointer hover:shadow-md transition-all group"
              data-testid={`companion-pick-${item.offer.business_id}`}
            >
              <div className="flex items-start gap-3">
                <div className="relative">
                  <MiniLogo name={item.offer.name} />
                  <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold ${
                    item.type === "urgent" ? "bg-red-500" : item.type === "value" ? "bg-green-500" : item.type === "easy" ? "bg-amber-500" : "bg-purple-500"
                  }`}>
                    {i + 1}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800">{item.offer.name}</p>
                  {getStatus(item.offer.business_id) && <UnifiedStatusBadge status={getStatus(item.offer.business_id)} />}
                  <p className="text-sm text-slate-500 mt-0.5 truncate">{item.offer.offer_title}</p>
                  <p className="text-xs text-sky-500 mt-1.5 italic">{item.message}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-lg font-bold text-green-600">${item.offer.offer_value_est}</span>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-sky-500 transition-colors" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Quick category access */}
      <div className="px-6 mt-8">
        <h2 className="text-xs font-bold text-slate-400 tracking-widest uppercase mb-4">Browse by Category</h2>
        <div className="grid grid-cols-2 gap-3">
          {topCats.map(([cat, count]) => {
            const Icon = catIcons[cat] || Gift;
            return (
              <motion.div
                key={cat}
                whileTap={{ scale: 0.97 }}
                className="bg-slate-50 rounded-2xl p-4 flex items-center gap-3 cursor-pointer hover:bg-slate-100 transition-colors"
                data-testid={`companion-cat-${cat.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Icon className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm font-semibold text-slate-700">{cat}</p>
                  <p className="text-xs text-slate-400">{count} offers</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* CTA to see all */}
      <div className="px-6 mt-8">
        <button className="w-full py-4 bg-slate-900 text-white rounded-2xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:bg-slate-800 transition-colors" data-testid="companion-see-all">
          See All {offers.length} Deals <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
